import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { CUT_POSITION, PRESETS } from '../lib/presets'
import { formatDate } from '../lib/datetime'

interface Row {
  id: string
  season: number
  major: string | null
  name: string
  status: string
  starts_at: string
  dg_event_id: string | null
  counts_toward_marathon: boolean | null
}

export const MAJOR_LABEL: Record<string, string> = {
  masters: 'Masters',
  pga: 'PGA Championship',
  us_open: 'US Open',
  the_open: 'The Open',
}

export function AdminTournaments() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [season, setSeason] = useState(new Date().getFullYear() + 1)
  const [busy, setBusy] = useState(false)
  const [oneName, setOneName] = useState('')

  useEffect(() => {
    supabase
      .from('tournaments')
      .select('id, season, major, name, status, starts_at, dg_event_id, counts_toward_marathon')
      .order('season', { ascending: false })
      .order('starts_at')
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRows((data as Row[]) ?? [])
        setLoading(false)
      })
  }, [])

  if (profile && profile.role !== 'admin') {
    return (
      <div className="page">
        <div className="notice notice--error">Den här sidan är bara för admin.</div>
      </div>
    )
  }

  async function createSingle() {
    setBusy(true)
    setError(null)

    // Startar om en vecka som utkast — tider och regler justeras efteråt.
    const week = 7 * 24 * 60 * 60 * 1000
    const start = new Date(Date.now() + week)
    const { data, error } = await supabase
      .from('tournaments')
      .insert({
        season: new Date().getFullYear(),
        major: null,
        name: oneName.trim(),
        par: 72,
        starts_at: start.toISOString(),
        lock_time: new Date(start.getTime() - 60 * 60 * 1000).toISOString(),
        start_delay_minutes: 10,
        status: 'draft',
        cut_position: 65,
        ruleset: PRESETS.find((p) => p.key === 'blank')?.ruleset ?? {},
      })
      .select('id')
      .single()

    setBusy(false)
    if (error) setError(error.message)
    else navigate(`/admin/turnering/${data.id}`)
  }

  async function createSeason() {
    setBusy(true)
    setError(null)

    // En hel säsong läggs upp på en gång — fyra majors i spelordning, som
    // utkast. Datum och regler justeras sedan per turnering.
    const majors: Array<{ major: string; month: number; day: number; preset: string }> = [
      { major: 'masters', month: 3, day: 9, preset: 'blank' },
      { major: 'pga', month: 4, day: 14, preset: 'pga_2026' },
      { major: 'us_open', month: 5, day: 18, preset: 'us_open_2026' },
      { major: 'the_open', month: 6, day: 16, preset: 'the_open_2026' },
    ]

    const payload = majors.map((m) => {
      const start = new Date(Date.UTC(season, m.month, m.day, 12, 0, 0))
      const lock = new Date(start.getTime() - 60 * 60 * 1000)
      return {
        season,
        major: m.major,
        name: `${MAJOR_LABEL[m.major]} ${season}`,
        par: 72,
        starts_at: start.toISOString(),
        lock_time: lock.toISOString(),
        start_delay_minutes: 10,
        status: 'draft',
        cut_position: CUT_POSITION[m.major] ?? 60,
        ruleset: PRESETS.find((p) => p.key === m.preset)?.ruleset ?? {},
      }
    })

    const { error } = await supabase.from('tournaments').insert(payload)
    setBusy(false)

    if (error) setError(error.message)
    else navigate(0)
  }

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">Admin</p>
        <h1>Turneringar</h1>
      </div>

      <div>
        <Link to="/admin/koder"><button>Koder för nya gäng</button></Link>{' '}
        <Link to="/admin/anvandare"><button>Deltagare</button></Link>{' '}
        <Link to="/admin/gang"><button>Gäng och medlemmar</button></Link>{' '}
        <Link to="/admin/segrare"><button>Segrare per major</button></Link>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar…</p>}

      {rows.map((t) => (
        <article key={t.id} className="card stack">
          <div>
            <p className="eyebrow">
              {t.major ? (MAJOR_LABEL[t.major] ?? t.major) : 'Tävling'} {t.season} · {t.status}
              {!t.major && t.counts_toward_marathon !== true && ' · utanför maraton'}
            </p>
            <h2>{t.name}</h2>
            <p className="muted">
              {formatDate(t.starts_at)}
              {t.dg_event_id ? ` · DataGolf event ${t.dg_event_id}` : ' · inget DataGolf-event satt'}
            </p>
          </div>
          <div>
            <Link to={`/admin/turnering/${t.id}`}>
              <button>Redigera</button>
            </Link>
          </div>
        </article>
      ))}

      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault()
          void createSeason()
        }}
      >
        <h2>Lägg upp en säsong</h2>
        <p className="muted">
          Skapar fyra majors som utkast, med preliminära datum och regeluppsättningar
          från 2026. Justera sedan varje turnering för sig.
        </p>
        <div>
          <label htmlFor="season">Säsong</label>
          <input
            id="season"
            type="number"
            min={2024}
            max={2100}
            value={season}
            onChange={(e) => setSeason(Number(e.target.value))}
            style={{ maxWidth: 140 }}
          />
        </div>
        <button className="primary" type="submit" disabled={busy}>
          {busy ? 'Skapar…' : `Skapa ${season} års majors`}
        </button>
      </form>

      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault()
          void createSingle()
        }}
      >
        <h2>Lägg upp en enskild tävling</h2>
        <p className="muted">
          The Players, en vanlig PGA-vecka eller ett genrep. Räknas inte i
          maratontabellen om du inte säger till.
        </p>
        <div>
          <label htmlFor="one-name">Namn</label>
          <input
            id="one-name"
            type="text"
            value={oneName}
            onChange={(e) => setOneName(e.target.value)}
            placeholder="Genrep — PGA-veckan"
          />
        </div>
        <button className="primary" type="submit" disabled={busy || !oneName.trim()}>
          {busy ? 'Skapar…' : 'Skapa tävling'}
        </button>
      </form>
    </div>
  )
}
